/**
 * VendorLeadTimesAdmin — settings panel for managing vendor lead-time defaults
 * used by the manual ordering packet system.
 *
 * Notes:
 *  • These are DEFAULTS. The ordering rep can override the expected delivery
 *    date per packet (Vendor & Payment tab) and per line item after placement.
 *  • Edits persist to DB via OrderingPacketContext.
 */
import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Truck, Plus, Save, RotateCcw, Trash2, Info } from "lucide-react";
import { useOrderingPacket } from "@/data/orderingPacket/context";
import { type VendorLeadTimeConfig } from "@/data/orderingPacket/types";
import { toast } from "sonner";

export function VendorLeadTimesAdmin() {
  const { vendorLeadTimes, setVendorLeadTimes, upsertVendorLeadTime, removeVendorLeadTime } = useOrderingPacket();
  const [draft, setDraft] = useState<VendorLeadTimeConfig>({
    vendorName: "",
    leadTimeDays: 7,
    isPrimary: false,
    notes: "",
  });

  const sorted = useMemo(
    () => [...vendorLeadTimes].sort((a, b) => {
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.vendorName.localeCompare(b.vendorName);
    }),
    [vendorLeadTimes],
  );

  const handleAdd = () => {
    const name = draft.vendorName.trim();
    if (!name) {
      toast.error("Vendor name is required");
      return;
    }
    if (vendorLeadTimes.some((v) => v.vendorName.toLowerCase() === name.toLowerCase())) {
      toast.error("A vendor with that name already exists");
      return;
    }
    upsertVendorLeadTime({ ...draft, vendorName: name });
    setDraft({ vendorName: "", leadTimeDays: 7, isPrimary: false, notes: "" });
    toast.success(`${name} added`);
  };

  const handleRowSave = (cfg: VendorLeadTimeConfig) => {
    upsertVendorLeadTime(cfg);
    toast.success(`${cfg.vendorName} updated`);
  };

  const handleReset = () => {
    setVendorLeadTimes([]);
    toast.success("Vendor lead times cleared");
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="h-4 w-4 text-primary" />
                Vendor Lead Times
              </CardTitle>
              <CardDescription className="text-[12px] mt-1">
                Default expected delivery time used when an ordering packet is created.
                The ordering rep can override the date per packet and per line item.
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleReset} className="h-8 text-[11px] gap-1.5">
              <RotateCcw className="h-3.5 w-3.5" />
              Clear all
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-info/10 border border-info/20">
            <Info className="h-3.5 w-3.5 text-info shrink-0 mt-0.5" />
            <div className="text-[11px] text-info-foreground/80 leading-relaxed">
              These defaults seed the <strong>Expected Delivery Date</strong> on new
              packets. After placing an order, the rep can revise the actual ETA
              based on the vendor's confirmation — every change is logged in the
              packet's audit trail.
            </div>
          </div>

          <div className="rounded-lg border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px] uppercase tracking-wider">Vendor</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider w-[140px] text-right">Lead Time (days)</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider w-[110px]">Primary</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Notes</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider w-[150px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((cfg) => (
                  <VendorRow
                    key={cfg.vendorName}
                    cfg={cfg}
                    onSave={handleRowSave}
                    onRemove={() => {
                      removeVendorLeadTime(cfg.vendorName);
                      toast.success(`${cfg.vendorName} removed`);
                    }}
                  />
                ))}
                {sorted.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-[11px] text-muted-foreground py-8">
                      No vendors configured. Add one below or reset to defaults.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Plus className="h-4 w-4 text-primary" />
            Add Vendor
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-12 gap-3 items-end">
            <div className="col-span-4">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Vendor Name</Label>
              <Input
                value={draft.vendorName}
                onChange={(e) => setDraft({ ...draft, vendorName: e.target.value })}
                placeholder="e.g. Ferguson"
                className="h-8 text-[12px] mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Lead Time (days)</Label>
              <Input
                type="number"
                min={0}
                value={draft.leadTimeDays}
                onChange={(e) => setDraft({ ...draft, leadTimeDays: Number(e.target.value) || 0 })}
                className="h-8 text-[12px] mt-1 text-right"
              />
            </div>
            <div className="col-span-2 flex items-center gap-2 pb-1.5">
              <Switch
                checked={draft.isPrimary}
                onCheckedChange={(checked) => setDraft({ ...draft, isPrimary: checked })}
              />
              <Label className="text-[11px]">Primary vendor</Label>
            </div>
            <div className="col-span-3">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Notes</Label>
              <Input
                value={draft.notes ?? ""}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder="optional"
                className="h-8 text-[12px] mt-1"
              />
            </div>
            <div className="col-span-1">
              <Button onClick={handleAdd} size="sm" className="h-8 w-full text-[11px] gap-1">
                <Plus className="h-3.5 w-3.5" /> Add
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ────────── ROW EDITOR ────────── */
function VendorRow({
  cfg, onSave, onRemove,
}: {
  cfg: VendorLeadTimeConfig;
  onSave: (cfg: VendorLeadTimeConfig) => void;
  onRemove: () => void;
}) {
  const [edit, setEdit] = useState<VendorLeadTimeConfig>(cfg);
  const dirty =
    edit.leadTimeDays !== cfg.leadTimeDays ||
    edit.isPrimary !== cfg.isPrimary ||
    (edit.notes ?? "") !== (cfg.notes ?? "");

  return (
    <TableRow>
      <TableCell className="text-[12px] py-2 font-medium">
        {cfg.vendorName}
        {cfg.isPrimary && (
          <Badge variant="outline" className="ml-2 text-[9px] py-0 px-1.5 border-primary/40 text-primary">
            primary
          </Badge>
        )}
      </TableCell>
      <TableCell className="text-right py-2">
        <Input
          type="number"
          min={0}
          value={edit.leadTimeDays}
          onChange={(e) => setEdit({ ...edit, leadTimeDays: Number(e.target.value) || 0 })}
          className="h-7 text-[11px] text-right w-[90px] ml-auto tabular-nums"
        />
      </TableCell>
      <TableCell className="py-2">
        <Switch
          checked={edit.isPrimary}
          onCheckedChange={(checked) => setEdit({ ...edit, isPrimary: checked })}
        />
      </TableCell>
      <TableCell className="py-2">
        <Input
          value={edit.notes ?? ""}
          onChange={(e) => setEdit({ ...edit, notes: e.target.value })}
          placeholder="—"
          className="h-7 text-[11px]"
        />
      </TableCell>
      <TableCell className="text-right py-2">
        <div className="flex items-center gap-1.5 justify-end">
          <Button
            size="sm"
            variant={dirty ? "default" : "outline"}
            disabled={!dirty}
            onClick={() => onSave(edit)}
            className="h-7 text-[10px] gap-1"
          >
            <Save className="h-3 w-3" /> Save
          </Button>
          <Button size="sm" variant="ghost" onClick={onRemove} className="h-7 text-[10px] text-destructive hover:text-destructive gap-1">
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
